import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { useRdv } from "./SiteExtras";
import { useAvailability } from "@/lib/ContentContext";
import DailyMessage from "./DailyMessage";
import MusicButton from "./MusicButton";

const MAIN = [
  { to: "/", label: "Accueil" },
  { to: "https://stats.sender.net/forms/bW636g/view", label: "Newsletter", external: true },
  { to: "/a-propos", label: "À propos" },
  { to: "/livres", label: "Livres", coll: "books" },
  { to: "/boutique", label: "Boutique", coll: "books" },
  { to: "/consultations", label: "Consultations" },
  { to: "/conferences", label: "Conférences" },
  { to: "/les-chroniques-de-letrange", label: "Les Chroniques de l'Étrange" },
  { to: "https://esoterismebook.dorotheelancelot.fr", label: "Ésotérisme Book", external: true },
  { to: "/presse-medias", label: "Presse & Médias", coll: "press" },
  { to: "/agenda", label: "Agenda", coll: "events" },
];

const MORE = [
  { to: "https://universdesreves.dorotheelancelot.fr", label: "L’Univers des Rêves", external: true },
  { to: "/dorothee-ia", label: "Dorothée IA" },
  { to: "/vrai-croyance-legende", label: "Vrai, croyance ou légende ?" },
  { to: "/univers", label: "Univers" },
  { to: "/onirotherapie", label: "Onirothérapie" },
  { to: "/hypnose", label: "Hypnose" },
  { to: "/mediumnite", label: "Médiumnité" },
  { to: "/deuil", label: "Deuil" },
  { to: "/histoire-esoterisme", label: "Histoire & Ésotérisme" },
  { to: "/podcast", label: "Podcast", coll: "podcasts" },
  { to: "/videos", label: "Vidéos", coll: "videos" },
  { to: "/projets", label: "Projets", coll: "projects" },
  { to: "/professionnels", label: "Professionnels" },
  { to: "/reseaux-sociaux", label: "Réseaux" },
];

export default function Navbar() {
  const header = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const rdv = useRdv();
  const avail = useAvailability();
  const has = (item) => !item.coll || (avail?.[item.coll] || 0) > 0;
  const main = MAIN.filter(has);
  const more = MORE.filter(has);

  useEffect(() => {
    const measure = () => {
      document.documentElement.style.setProperty(
        "--dl-header-height",
        `${header.current?.getBoundingClientRect().height || 74}px`
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(header.current);
    measure();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <header
      ref={header}
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(5,8,20,0.88)" : "rgba(5,8,20,0.35)",
        backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${scrolled ? "rgba(198,167,106,0.22)" : "transparent"}`,
      }}
    >
      <style>{`
        .dl-biography-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 52px;
          padding: 10px 24px;
          border: 2px solid #F3D98E;
          border-radius: 10px;
          background: #D4B978;
          color: #050814;
          font-size: clamp(18px, 2vw, 22px);
          font-weight: 800;
          line-height: 1.25;
          text-decoration: none;
          animation: dlBiographyBlink 2.4s ease-in-out infinite;
        }
        .dl-biography-button:hover {
          background: #F3D98E;
        }
        .dl-biography-button:hover,
        .dl-biography-button:focus-visible {
          animation: none;
        }
        .dl-biography-button:focus-visible {
          outline: 3px solid #F3E9D2;
          outline-offset: 4px;
        }
        @keyframes dlBiographyBlink {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 5px rgba(212,185,120,.25);
          }
          50% {
            opacity: .72;
            box-shadow: 0 0 24px rgba(243,217,142,.8);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .dl-biography-button { animation: none; }
        }
      `}</style>

      <div className="dl-container flex items-center py-2 border-b border-[#C6A76A]/20">
        <a
          href="https://biographie.dorotheelancelot.fr/"
          data-testid="nav-biographie"
          className="dl-biography-button"
        >
          Qui suis-je ?
        </a>
      </div>

      <div className="dl-container flex items-center justify-between h-[74px]">
        <div className="flex items-center gap-2 min-w-0">
          <MusicButton />
          <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
            <span className="serif text-[1.35rem] tracking-[0.18em] text-[#F3E9D2]">
              DOROTHÉE LANCELOT
            </span>
            <span className="dl-eyebrow mt-1 hidden sm:block" style={{ fontSize: "0.55rem" }}>
              Auteure • Médium • Conférencière • Onirothérapeute
            </span>
          </Link>
        </div>

        <nav className="hidden xl:flex items-center gap-6">
          {main.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={({ isActive }) =>
                `text-[0.72rem] tracking-[0.12em] uppercase font-medium transition-colors duration-300 ${
                  isActive ? "text-[#D4B978]" : "text-[#F3E9D2]/80 hover:text-[#D4B978]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
            onKeyDown={event => { if (event.key === "Escape") { setMoreOpen(false); event.currentTarget.querySelector("button")?.focus(); } }}
          >
            <button
              data-testid="nav-more-btn"
              onClick={() => setMoreOpen(true)}
              aria-expanded={moreOpen}
              aria-controls="official-more-navigation"
              className="flex items-center gap-1 text-[0.72rem] tracking-[0.12em] uppercase font-medium text-[#F3E9D2]/80 hover:text-[#D4B978] transition-colors"
            >
              Plus <ChevronDown size={13} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full pt-3 w-60" id="official-more-navigation" data-testid="nav-more-menu">
                <div className="border border-[rgba(198,167,106,0.25)] bg-[#0B1020] py-2 shadow-2xl">
                  {more.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`nav-more-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                      className="block px-5 py-2.5 text-[0.75rem] tracking-wide text-[#F3E9D2]/85 hover:text-[#D4B978] hover:bg-white/[0.03] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/recherche"
            data-testid="nav-search"
            aria-label="Rechercher"
            className="text-[#F3E9D2]/80 hover:text-[#D4B978] transition-colors"
          >
            <Search size={17} />
          </Link>
          <button
            data-testid="nav-rdv-btn"
            onClick={() => rdv?.openRdv?.()}
            className="ml-1 px-4 py-2 border border-[#C6A76A] text-[#D4B978] text-[0.66rem] tracking-[0.14em] uppercase font-semibold hover:bg-[#C6A76A] hover:text-[#050814] transition-colors"
          >
            Rendez-vous
          </button>
        </nav>

        <div className="xl:hidden flex items-center gap-3">
          <Link to="/recherche" aria-label="Rechercher" className="text-[#D4B978] p-1">
            <Search size={22} />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="text-[#D4B978] p-2"
            onClick={() => setOpen((value) => !value)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {!open && (
        <div className="dl-container dl-daily-row">
          <DailyMessage />
        </div>
      )}

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#050814] border-t border-[rgba(198,167,106,0.2)] max-h-[80vh] overflow-y-auto"
        >
          <div className="dl-container py-6 flex flex-col gap-1">
            {[...main, ...more].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className={({ isActive }) =>
                  `py-3 text-sm tracking-wide uppercase border-b border-white/5 ${
                    isActive ? "text-[#D4B978]" : "text-[#F3E9D2]/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-rdv-btn"
              onClick={() => {
                setOpen(false);
                rdv?.openRdv?.();
              }}
              className="mt-4 dl-btn dl-btn-solid justify-center"
            >
              Prendre rendez-vous
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
